import React from 'react';
import { OnboardingStatusResponse } from '../types';

interface OnboardingWelcomeProps {
  token: string;
  onboardingData: OnboardingStatusResponse;
  onStepComplete: (step: string, data?: Record<string, unknown>) => Promise<void>;
  error: string | null;
}

/**
 * Welcome screen for Fair Manager onboarding
 * Issue #1455 - Step 1 of 7: Overview and estimated time
 */
const OnboardingWelcome: React.FC<OnboardingWelcomeProps> = ({
  onboardingData: { fair, onboarding },
  onStepComplete,
}) => {
  const expirationDate = new Date(onboarding.token_expires_at).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const handleGetStarted = async () => {
    try {
      await onStepComplete('tos');
    } catch (err) {
      console.error("Failed to proceed:", err);
      console.error('Failed to proceed:', err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4" aria-hidden="true">🎉</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Congratulations! Your Application Was Approved
        </h2>
        <p className="text-lg text-gray-600">
          You're now ready to manage <strong>{fair.name}</strong>
        </p>
      </div>

      <div className="bg-teal-50 border border-teal-200 rounded-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-teal-900 mb-3">What's Next?</h3>
        <p className="text-teal-800 mb-4">
          Complete this quick onboarding process to set up your fair manager profile and get full
          access to manage your fair. This will take approximately <strong>5-10 minutes</strong>.
        </p>
        <ul className="space-y-2 text-teal-800">
          <li className="flex items-start">
            <svg className="h-5 w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Review and accept our Terms of Service</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Complete your fair manager profile</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Add contact information (optional)</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Set up payment details (optional, can be done later)</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Review and confirm your fair details</span>
          </li>
        </ul>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
        <div className="flex">
          <svg className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-yellow-900">Link Expires Soon</h4>
            <p className="text-sm text-yellow-800 mt-1">
              This onboarding link will expire on <strong>{expirationDate}</strong>. Please
              complete the process before this date to avoid delays.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-8">
        <div className="flex items-start">
          <svg className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-blue-900">Progress Saved Automatically</h4>
            <p className="text-sm text-blue-800 mt-1">
              You can pause and resume this process at any time. Your progress will be saved
              automatically, so feel free to take breaks as needed.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleGetStarted}
          className="px-8 py-3 bg-teal-600 text-white text-lg font-semibold rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
        >
          Get Started
        </button>
      </div>
    </div>
  );
};

export default OnboardingWelcome;
